import { formatValue } from './formatters';
import {
    HIDDEN_FIELDS,
    LONG_TEXT_FIELDS,
    DNS_FIELDS,
    SEO_FIELDS,
    SITE_TYPE_FIELDS,
    CUSTOMER_FIT_FIELDS,
    DONATION_FIELDS
} from './fieldConfig';

export const normalizeAnswers = (answers) => {
    if (!answers) {
        return { normalizedAnswers: null, gatekeeperData: null };
    }

    const normalizedAnswers = { ...answers };
    let gatekeeperData = null;

    if (normalizedAnswers.gatekeeper) {
        gatekeeperData = normalizedAnswers.gatekeeper;
        delete normalizedAnswers.gatekeeper;
    }

    const hasPlatform = normalizedAnswers.platform !== undefined && normalizedAnswers.platform !== null;
    const hasPlatformConfidence = normalizedAnswers.platform_confidence !== undefined && normalizedAnswers.platform_confidence !== null;

    if (hasPlatform && hasPlatformConfidence) {
        const platformLabel = formatValue(normalizedAnswers.platform);
        const platformConfidence = formatValue(normalizedAnswers.platform_confidence);
        normalizedAnswers.platform = `${platformLabel} (${platformConfidence})`;
        delete normalizedAnswers.platform_confidence;
    }

    const hasModernityLabel = normalizedAnswers.technology_modernity_label !== undefined && normalizedAnswers.technology_modernity_label !== null;
    const hasModernityRating = normalizedAnswers.technology_modernity_rating !== undefined && normalizedAnswers.technology_modernity_rating !== null;

    if (hasModernityLabel && hasModernityRating) {
        const modernityLabel = formatValue(normalizedAnswers.technology_modernity_label);
        const modernityRating = formatValue(normalizedAnswers.technology_modernity_rating);
        normalizedAnswers.technology_modernity_label = `${modernityLabel} (${modernityRating})`;
        delete normalizedAnswers.technology_modernity_rating;
    }

    // Convert third_party_integrations array to boolean
    if (normalizedAnswers.third_party_integrations !== undefined) {
        const hasThirdPartyIntegrations = Array.isArray(normalizedAnswers.third_party_integrations) 
            ? normalizedAnswers.third_party_integrations.length > 0
            : Boolean(normalizedAnswers.third_party_integrations);
        normalizedAnswers.has_third_party_integrations = hasThirdPartyIntegrations;
        // Keep the original array for the URLs section, but don't show it in answer cards
    }

    // Convert collect_forms object to count of forms
    if (normalizedAnswers.collect_forms !== undefined && normalizedAnswers.collect_forms !== null) {
        const collectForms = normalizedAnswers.collect_forms;
        const formsArray = collectForms?.forms || (Array.isArray(collectForms) ? collectForms : []);
        normalizedAnswers.collect_forms = Array.isArray(formsArray) ? formsArray.length : 0;
    }

    // Convert has_socialmedia object to boolean
    if (normalizedAnswers.has_socialmedia !== undefined) {
        const socialMedia = normalizedAnswers.has_socialmedia;
        // If it's an object with a value property, use that; otherwise convert to boolean
        const hasSocialMedia = socialMedia && typeof socialMedia === 'object' && socialMedia !== null
            ? Boolean(socialMedia.value)
            : Boolean(socialMedia);
        normalizedAnswers.has_socialmedia = hasSocialMedia;
        // Keep the original object in answers for the Social Media tab (accessed via raw answers)
    }

    // Convert website_languages object to count of languages
    if (normalizedAnswers.website_languages !== undefined && normalizedAnswers.website_languages !== null) {
        const websiteLanguages = normalizedAnswers.website_languages;
        const languagesArray = websiteLanguages?.languages || (Array.isArray(websiteLanguages) ? websiteLanguages : []);
        normalizedAnswers.website_languages = Array.isArray(languagesArray) ? languagesArray.length : 0;
    }

    // Convert newsletter object to boolean
    if (normalizedAnswers.newsletter !== undefined) {
        const newsletter = normalizedAnswers.newsletter;
        // If it's an object with a value property, use that; otherwise convert to boolean
        const hasNewsletter = newsletter && typeof newsletter === 'object' && newsletter !== null
            ? Boolean(newsletter.value)
            : Boolean(newsletter);
        normalizedAnswers.newsletter = hasNewsletter;
        // Keep the original object in answers for potential future use (accessed via raw answers)
    }

    // Convert has_newsletter object to boolean
    if (normalizedAnswers.has_newsletter !== undefined) {
        const hasNewsletter = normalizedAnswers.has_newsletter;
        // If it's an object with a value property, use that; otherwise convert to boolean
        const newsletterValue = hasNewsletter && typeof hasNewsletter === 'object' && hasNewsletter !== null
            ? Boolean(hasNewsletter.value)
            : Boolean(hasNewsletter);
        normalizedAnswers.has_newsletter = newsletterValue;
        // Keep the original object in answers for potential future use (accessed via raw answers)
    }

    // Convert has_blog object to boolean
    if (normalizedAnswers.has_blog !== undefined) {
        const hasBlog = normalizedAnswers.has_blog;
        // If it's an object with a value property, use that; otherwise convert to boolean
        const blogValue = hasBlog && typeof hasBlog === 'object' && hasBlog !== null
            ? Boolean(hasBlog.value)
            : Boolean(hasBlog);
        normalizedAnswers.has_blog = blogValue;
        // Keep the original object in answers for potential future use (accessed via raw answers)
    }

    // Convert has_contact_form object to boolean
    if (normalizedAnswers.has_contact_form !== undefined) {
        const hasContactForm = normalizedAnswers.has_contact_form;
        // If it's an object with a value property, use that; otherwise convert to boolean
        const contactFormValue = hasContactForm && typeof hasContactForm === 'object' && hasContactForm !== null
            ? Boolean(hasContactForm.value)
            : Boolean(hasContactForm);
        normalizedAnswers.has_contact_form = contactFormValue;
        // Keep the original object in answers for potential future use (accessed via raw answers)
    }

    return { normalizedAnswers, gatekeeperData };
};

export const processGatekeeperData = (gatekeeperData, evaluation, website) => {
    if (!gatekeeperData && evaluation?.gatekeeper) {
        gatekeeperData = evaluation.gatekeeper;
    }

    if (!gatekeeperData) {
        return null;
    }

    const gatekeeperShouldContinue = gatekeeperData?.should_continue ?? null;
    const gatekeeperRejected = gatekeeperShouldContinue === false || evaluation?.skipped || website?.flags?.gatekeeper_rejected;

    return {
        gatekeeperData,
        gatekeeperShouldContinue,
        gatekeeperRejected,
        gatekeeperConfidence: gatekeeperData?.confidence ?? null,
        gatekeeperCategory: gatekeeperData?.category ?? null,
        gatekeeperReason: gatekeeperData?.reason ?? null
    };
};

export const processSignals = (signalsRaw) => {
    if (!signalsRaw) {
        return [];
    }

    if (Array.isArray(signalsRaw)) {
        return signalsRaw.filter(Boolean).map(item => formatValue(item));
    }

    if (typeof signalsRaw === 'string') {
        return signalsRaw
            .split('\n')
            .map(item => item.trim())
            .filter(Boolean);
    }

    return [formatValue(signalsRaw)].filter(Boolean);
};

export const processConsoleLogs = (rawConsoleLogs) => {
    if (!rawConsoleLogs) {
        return [];
    }

    if (Array.isArray(rawConsoleLogs)) {
        return rawConsoleLogs;
    }

    if (typeof rawConsoleLogs === 'string') {
        try {
            const parsed = JSON.parse(rawConsoleLogs);
            if (Array.isArray(parsed)) {
                return parsed;
            }
            if (parsed && Array.isArray(parsed.logs)) {
                return parsed.logs;
            }
            return [];
        } catch (error) {
            console.error('Failed to parse console_logs JSON.', error);
            return [];
        }
    }

    if (typeof rawConsoleLogs === 'object' && rawConsoleLogs !== null) {
        if (Array.isArray(rawConsoleLogs.logs)) {
            return rawConsoleLogs.logs;
        }
        return [];
    }

    return [];
};

export const categorizeAnswers = (normalizedAnswers) => {
    if (!normalizedAnswers) {
        return {
            answerEntries: [],
            standardAnswers: [],
            donationAnswers: [],
            dnsAnswers: [],
            seoAnswers: [],
            longTextAnswers: []
        };
    }

    const answerEntries = Object.entries(normalizedAnswers).filter(([field]) => !HIDDEN_FIELDS.has(field));

    return {
        answerEntries,
        standardAnswers: answerEntries.filter(([field]) => 
            !LONG_TEXT_FIELDS.includes(field) && 
            !DNS_FIELDS.has(field) && 
            !SEO_FIELDS.has(field) && 
            !SITE_TYPE_FIELDS.has(field) && 
            !CUSTOMER_FIT_FIELDS.has(field) && 
            !DONATION_FIELDS.has(field) &&
            !field.endsWith('_urls') && // Filter out _urls fields
            field !== 'third_party_integrations' // Filter out array, use has_third_party_integrations boolean instead
        ),
        donationAnswers: answerEntries.filter(([field]) => DONATION_FIELDS.has(field)),
        dnsAnswers: answerEntries.filter(([field]) => DNS_FIELDS.has(field)),
        seoAnswers: answerEntries.filter(([field]) => SEO_FIELDS.has(field)),
        longTextAnswers: answerEntries.filter(([field]) => LONG_TEXT_FIELDS.includes(field))
    };
};

export const extractUrlData = (normalizedAnswers) => {
    if (!normalizedAnswers) {
        return {
            contactFormUrls: [],
            blogUrls: [],
            productGalleryUrls: [],
            googleMapsUrls: [],
            thirdPartyIntegrationsUrls: {}
        };
    }

    return {
        contactFormUrls: Array.isArray(normalizedAnswers.has_contact_form_urls) 
            ? normalizedAnswers.has_contact_form_urls 
            : [],
        blogUrls: Array.isArray(normalizedAnswers.has_blog_urls) 
            ? normalizedAnswers.has_blog_urls 
            : [],
        productGalleryUrls: Array.isArray(normalizedAnswers.has_product_gallery_urls) 
            ? normalizedAnswers.has_product_gallery_urls 
            : [],
        googleMapsUrls: Array.isArray(normalizedAnswers.has_google_maps_urls) 
            ? normalizedAnswers.has_google_maps_urls 
            : [],
        thirdPartyIntegrationsUrls: typeof normalizedAnswers.third_party_integrations_urls === 'object' && 
            normalizedAnswers.third_party_integrations_urls !== null &&
            !Array.isArray(normalizedAnswers.third_party_integrations_urls)
            ? normalizedAnswers.third_party_integrations_urls 
            : {}
    };
};

export const processSiteTypeData = (normalizedAnswers) => {
    if (!normalizedAnswers) {
        return null;
    }

    return {
        type: normalizedAnswers.site_type ?? null,
        subType: normalizedAnswers.site_sub_type ?? null,
        confidence: normalizedAnswers.site_type_confidence ?? null,
        primaryCta: normalizedAnswers.site_type_primary_call_to_action ?? null,
        notes: normalizedAnswers.site_type_notes ?? null,
        signals: normalizedAnswers.site_type_signals ?? null
    };
};

export const processCustomerFitData = (normalizedAnswers) => {
    if (!normalizedAnswers) {
        return {
            customerFitRating: null,
            customerFitConfidence: null,
            customerFitSignalsRaw: null,
            customerFitRecommendedMotion: null,
            hasCustomerFitRating: false,
            hasCustomerFitConfidence: false,
            hasCustomerFitSignals: false,
            hasCustomerFitRecommendedMotion: false
        };
    }

    const customerFitRating = normalizedAnswers.customer_fit_rating ?? null;
    const customerFitConfidence = normalizedAnswers.customer_fit_confidence ?? null;
    const customerFitSignalsRaw = normalizedAnswers.customer_fit_signals ?? null;
    const customerFitRecommendedMotion = normalizedAnswers.customer_fit_recommended_motion ?? null;

    return {
        customerFitRating,
        customerFitConfidence,
        customerFitSignalsRaw,
        customerFitRecommendedMotion,
        hasCustomerFitRating: customerFitRating !== null && customerFitRating !== undefined && customerFitRating !== '',
        hasCustomerFitConfidence: customerFitConfidence !== null && customerFitConfidence !== undefined && customerFitConfidence !== '',
        hasCustomerFitSignals: Array.isArray(customerFitSignalsRaw)
            ? customerFitSignalsRaw.length > 0
            : Boolean(customerFitSignalsRaw),
        hasCustomerFitRecommendedMotion: customerFitRecommendedMotion !== null && customerFitRecommendedMotion !== undefined && customerFitRecommendedMotion !== ''
    };
};

export const LOG_LEVEL_SUMMARY = [
    { key: 'SEVERE', label: 'Severe', color: '#b71c1c' },
    { key: 'ERROR', label: 'Errors', color: '#dc3545' },
    { key: 'WARN', label: 'Warnings', color: '#ffc107', aliases: ['WARNING'] },
    { key: 'INFO', label: 'Info', color: '#64b5f6' },
    { key: 'DEBUG', label: 'Debug', color: '#17a2b8' }
];

export const processConsoleLogLevelSummary = (consoleLogs) => {
    const counts = new Map(LOG_LEVEL_SUMMARY.map(({ key }) => [key, 0]));
    let otherCount = 0;

    for (const log of consoleLogs) {
        if (!log) {
            continue;
        }

        const rawLevel = typeof log.level === 'string' ? log.level.toUpperCase() : 'UNKNOWN';
        const match = LOG_LEVEL_SUMMARY.find(({ key, aliases }) => key === rawLevel || (aliases && aliases.includes(rawLevel)));

        if (match) {
            counts.set(match.key, (counts.get(match.key) ?? 0) + 1);
        } else {
            otherCount += 1;
        }
    }

    const summary = LOG_LEVEL_SUMMARY.map(({ key, label, color }) => ({
        key,
        label,
        color,
        count: counts.get(key) ?? 0
    })).filter(({ count }) => count > 0);

    if (otherCount > 0) {
        summary.push({ key: 'OTHER', label: 'Other', color: '#6c757d', count: otherCount });
    }

    return summary;
};

export const DEFAULT_METRIC_NAMES = ['FirstMeaningfulPaint', 'DomContentLoaded', 'TaskDuration'];

export const processPerformanceMetrics = (metricsList) => {
    if (metricsList.length === 0) {
        return { defaultMetrics: [], navigationStartValue: null };
    }

    const metricsByName = new Map(metricsList.map(metric => [metric.name, metric]));

    const selectedMetrics = DEFAULT_METRIC_NAMES
        .map(name => metricsByName.get(name))
        .filter(Boolean);

    if (selectedMetrics.length < DEFAULT_METRIC_NAMES.length) {
        const seen = new Set(selectedMetrics.map(metric => metric.name));
        for (const metric of metricsList) {
            if (seen.size === DEFAULT_METRIC_NAMES.length) {
                break;
            }
            if (!seen.has(metric.name)) {
                selectedMetrics.push(metric);
                seen.add(metric.name);
            }
        }
    }

    return {
        defaultMetrics: selectedMetrics.slice(0, DEFAULT_METRIC_NAMES.length),
        navigationStartValue: metricsByName.get('NavigationStart')?.value ?? null
    };
};

export const processDefaultMetricsDisplay = (defaultMetrics, navigationStartValue, formatValue) => {
    const shouldShowDelta = new Set(['FirstMeaningfulPaint', 'DomContentLoaded']);

    return defaultMetrics.map(metric => {
        if (navigationStartValue === null || !shouldShowDelta.has(metric.name)) {
            return {
                ...metric,
                displayValue: formatValue(metric.value)
            };
        }

        const deltaMs = Math.max(0, (metric.value - navigationStartValue) * 1000);
        const rounded = Math.round(deltaMs);

        return {
            ...metric,
            displayValue: `${rounded} ms`
        };
    });
};


